import { makeAutoObservable } from "mobx";
import { isNameValid, validMaxAge, validMinAge } from "../../../utils/validators";
import { FetchShelf, LoaderShelf } from "../../../shelves";
import { api } from "../../../services";
import { invertDate, switchSlashesToHyphens } from "../../../utils/formatters";
import type { AuthenticationStore, FintechRegisterStore } from "../../../stores";
import type { apiTypes } from "../../../services";

export class Store {
	public loader = new LoaderShelf();
	public patchCustomersPersonalFetch: FetchShelf<apiTypes.PersonalInfo>;
	public postPersonaAffiliationFetch: FetchShelf<apiTypes.PersonalAffiliationInfo>;
	public patchPersonaAffiliationFetch: FetchShelf<apiTypes.PersonalAffiliationInfo>;

	constructor(public authentication: AuthenticationStore, public fintechRegisterStore: FintechRegisterStore) {
		const authToken = authentication.getToken() || "";

		this.patchCustomersPersonalFetch = new FetchShelf(async () =>
			api.patchCustomersPersonal(this.personalInfo, authToken),
		);
		this.postPersonaAffiliationFetch = new FetchShelf(async () =>
			api.postCustomersPersonalAffiliation(this.personalAffiliationInfo, authToken),
		);
		this.patchPersonaAffiliationFetch = new FetchShelf(async () =>
			api.patchCustomersPersonalAffiliation(this.personalAffiliationInfo, authToken),
		);

		makeAutoObservable(this);
	}

	public get personalInfo(): apiTypes.PersonalInfo {
		return {
			cpfNumber: this.authentication.currentUser?.documentNumber || "",
			birthDate: invertDate(switchSlashesToHyphens(this.fintechRegisterStore.birthDate.field.value)),
			fullName: this.trimString(this.fintechRegisterStore.name.field.value),
		};
	}

	public get personalAffiliationInfo(): apiTypes.PersonalAffiliationInfo {
		return {
			fullName: this.trimString(this.fintechRegisterStore.motherName.field.value.replaceAll(".", "")),
			socialName: this.trimString(this.fintechRegisterStore.motherName.field.value.replaceAll(".", "")),
			type: "MAE",
		};
	}

	public get isSubmitButtonDisabled(): boolean {
		if (this.fintechRegisterStore.birthDate.field.value) {
			return (
				!validMinAge(this.fintechRegisterStore.birthDate.field.value, 18) ||
				!validMaxAge(this.fintechRegisterStore.birthDate.field.value) ||
				!isNameValid(this.fintechRegisterStore.motherName.field.value)
			);
		}

		return this.isLoading;
	}

	public get isLoading(): boolean {
		return this.patchCustomersPersonalFetch.loader.isLoading;
	}

	public fetchAffiliation = async (): Promise<void> => {
		if (this.fintechRegisterStore.motherAffiliationId.value) {
			return await this.patchPersonaAffiliationFetch.fetch();
		}

		return await this.postPersonaAffiliationFetch.fetch();
	};

	public fetchData = async (onSuccess: VoidFunction): Promise<void> => {
		if (this.loader.isLoading) {
			return;
		}

		try {
			this.loader.start();
			await this.fetchAffiliation();
			await this.patchCustomersPersonalFetch.fetch();

			onSuccess();
		} catch (e) {
			window.console.error(e);
		} finally {
			this.loader.end();
		}
	};

	// função privada para remover espaços em branco de uma string ou retornar uma string vazia
	private trimString = (value: string | undefined): string => value?.trim() || "";
}
